import Button from "atoms/buttons/button";
import ButtonGroup from "organisms/button-groups/button-group";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import Loader from "atoms/loaders/loader";
import PdfTableOfContentsEntry from "models/interfaces/pdf-table-of-contents-entry";
import React, { useState } from "react";
import TableOfContentsCollapsePanelHeader from "./table-of-contents-collapse-panel-header";
import TableOfContentsCollapsePanelLink from "./table-of-contents-collapse-panel-link";
import usePageErrors from "utilities/hooks/use-page-errors";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "utilities/collection-utils";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import { useLocalization } from "utilities/hooks/use-localization";
import { useMobileTableOfContentsContext } from "./mobile-table-of-contents-context";
import { IconSizes } from "atoms/constants/icon-sizes";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface PdfTableOfContentsProps {
    activeId: number | undefined;
    disableExpandOrCollapseAll?: boolean;
    loading?: boolean;
    setCurrentPageNumber: (value: number) => void;
    setActiveId: (value: number | undefined) => void;
    tableOfContentsEntries: PdfTableOfContentsEntry[];
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const PdfNav: React.FunctionComponent<PdfTableOfContentsProps> = (
    props: PdfTableOfContentsProps
) => {
    const {
        activeId,
        disableExpandOrCollapseAll = false,
        loading,
        setCurrentPageNumber,
        setActiveId,
        tableOfContentsEntries,
    } = props;

    // -----------------------------------------------------------------------------------------
    // #region Constants
    // -----------------------------------------------------------------------------------------

    const cssBaseClass = "c-table-of-contents";
    const CSS_NAV_CLASS = "c-image-viewer__nav";

    // #endregion Constants

    // -----------------------------------------------------------------------------------------
    // #region Custom Hooks
    // -----------------------------------------------------------------------------------------

    const { pageErrors } = usePageErrors();
    const { t } = useLocalization();

    const { onClick: onModalClose } = useMobileTableOfContentsContext();

    // #endregion Custom Hooks

    // -----------------------------------------------------------------------------------------
    // #region State
    // -----------------------------------------------------------------------------------------

    const [allCollapsed, setAllCollapsed] = useState<boolean | null>(null);

    // #endregion State

    // -----------------------------------------------------------------------------------------
    // #region Side-Effects
    // -----------------------------------------------------------------------------------------

    useErrorBoundary(pageErrors);

    // #endregion Side-Effects

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    if (loading) {
        return (
            <div className={cssBaseClass}>
                <Loader />
            </div>
        );
    }

    const onEntryClick = (entry: PdfTableOfContentsEntry) => {
        onModalClose();

        if (!entry.id || !entry.pageNumber) return;
        setCurrentPageNumber(entry.pageNumber);
        setActiveId(entry.id);
    };

    return (
        <div className={cssBaseClass}>
            {!disableExpandOrCollapseAll && (
                <div className={`${cssBaseClass}__collapseButtons`}>
                    <ButtonGroup>
                        <Button
                            onClick={() => setAllCollapsed(false)}
                            size={ButtonSizes.Small}
                            style={ButtonStyles.Secondary}>
                            {t("expandAll")}
                        </Button>
                        <Button
                            onClick={() => {
                                setAllCollapsed(true);
                            }}
                            size={ButtonSizes.Small}
                            style={ButtonStyles.Secondary}>
                            {t("collapseAll")}
                        </Button>
                    </ButtonGroup>
                </div>
            )}
            <nav className={CSS_NAV_CLASS}>
                <PdfTableOfContents
                    activeId={activeId}
                    allCollapsed={allCollapsed ?? undefined}
                    onEntryClick={onEntryClick}
                    tableOfContentsEntries={tableOfContentsEntries}
                />
            </nav>
        </div>
    );

    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

// #endregion Exports

interface componentProps {
    activeId: number | undefined;
    allCollapsed?: boolean;
    onEntryClick(entry: PdfTableOfContentsEntry): void;
    tableOfContentsEntries: PdfTableOfContentsEntry[];
}

const PdfTableOfContents = ({
    activeId,
    allCollapsed,
    onEntryClick,
    tableOfContentsEntries,
}: componentProps) => {
    const CSS_NAV_CLASS = "-nav-list";

    const isActive = (itemId: number | undefined): boolean => {
        return activeId != null && itemId === activeId;
    };

    const hasActiveChild = (entry: PdfTableOfContentsEntry): boolean => {
        if (isActive(entry.id)) return true;

        if (CollectionUtils.hasValues(entry.sections)) {
            return entry.sections.some(hasActiveChild);
        }

        return false;
    };

    const shouldCollapse = (entry: PdfTableOfContentsEntry): boolean => {
        if (allCollapsed != null) return allCollapsed;
        return !hasActiveChild(entry);
    };

    return tableOfContentsEntries.map((entry) => {
        const hasSections = CollectionUtils.hasValues(entry.sections);

        const isActiveEntry = isActive(entry.id);

        const className = StringUtils.joinClassNames([
            "c-table-of-contents-chapter",
            isActiveEntry ? "-active" : "",
            hasSections ? "" : "-empty",
        ]);

        return (
            <div className={className} key={entry.id}>
                <CollapsePanel
                    modifierCSSClassName="-panel -sub-panel-expanded"
                    data-test-id="chapter-collapse-panel"
                    panelTop={
                        <TableOfContentsCollapsePanelHeader
                            displayTitle={entry.heading}
                            description={entry.description}
                            isPdfPublication={true}
                            isPublic={false}
                            onClick={() => onEntryClick(entry)}
                        />
                    }
                    collapse={shouldCollapse(entry)}
                    buttonAriaText={entry.heading}
                    iconSize={IconSizes.Base}
                    scrollToWhenActive={isActiveEntry}>
                    {hasSections && (
                        <ul className={CSS_NAV_CLASS}>
                            {entry.sections.map((s, idx) => {
                                const hasSubsections =
                                    CollectionUtils.hasValues(s.sections);

                                if (!hasSubsections) {
                                    return (
                                        <div
                                            className="c-table-of-contents-chapter -empty -panel -sub-header"
                                            key={s.id}>
                                            <TableOfContentsCollapsePanelHeader
                                                onClick={() => onEntryClick(s)}
                                                displayTitle={s.heading}
                                                description={s.description}
                                                isPdfPublication={true}
                                            />
                                        </div>
                                    );
                                }

                                const className = StringUtils.joinClassNames([
                                    "c-table-of-contents-chapter",
                                    "-sub-header",
                                    "-sub-panel",
                                    s.id === activeId ? "-active" : "",
                                ]);

                                const parentActive =
                                    isActive(entry.id) && idx === 0;
                                const collapse =
                                    shouldCollapse(s) && !parentActive;

                                return (
                                    <div className={className} key={s.id}>
                                        <CollapsePanel
                                            key={s.id}
                                            panelTop={
                                                <TableOfContentsCollapsePanelHeader
                                                    onClick={() =>
                                                        onEntryClick(s)
                                                    }
                                                    displayTitle={s.heading}
                                                    description={s.description}
                                                    isPdfPublication={true}
                                                />
                                            }
                                            collapse={collapse}
                                            modifierCSSClassName="-panel -sub-panel -sub-panel-expanded"
                                            buttonAriaText={s.heading}
                                            iconSize={IconSizes.Base}>
                                            <ul className={CSS_NAV_CLASS}>
                                                {s.sections.map((ss, i) => {
                                                    // Check if the current section is active or, if it's the first item, if its parent is active
                                                    const active =
                                                        isActive(ss.id) ||
                                                        (i === 0 &&
                                                            (isActive(s.id) ||
                                                                (idx === 0 &&
                                                                    entry.id ===
                                                                        activeId)));
                                                    return (
                                                        <TableOfContentsCollapsePanelLink
                                                            key={ss.id}
                                                            index={ss.id}
                                                            isPublic={false}
                                                            onClick={() =>
                                                                onEntryClick(ss)
                                                            }
                                                            isActive={active}
                                                            isPdfPublication={
                                                                true
                                                            }>
                                                            <span>{`${ss.heading} ${ss.description}`}</span>
                                                        </TableOfContentsCollapsePanelLink>
                                                    );
                                                })}
                                            </ul>
                                        </CollapsePanel>
                                    </div>
                                );
                            })}
                        </ul>
                    )}
                </CollapsePanel>
            </div>
        );
    });
};

export default PdfNav;
